// src/polaris-components/PaymentFailureBanner.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isAdmin } from '../utils/auth';
import '../styles/payment-failure-banner.css';

interface PaymentFailureBannerProps {
  daysRemaining: number;
  onClose?: () => void;
}

/**
 * Banner component displayed when user's payment is past due
 * Shows warning message and link to update payment method
 */
export const PaymentFailureBanner: React.FC<PaymentFailureBannerProps> = ({
  daysRemaining,
  onClose
}) => {
  const navigate = useNavigate();
  const userIsAdmin = isAdmin();

  const handleUpdatePayment = () => {
    navigate('/billing');
  };

  return (
    <div
      className="payment-failure-banner"
      role="alert"
      data-testid="payment-failure-banner"
    >
      <div className="payment-failure-banner__content">
        <div className="payment-failure-banner__icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 2L2 7L12 12L22 7L12 2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 17L12 22L22 17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 12L12 17L22 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 12V2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="payment-failure-banner__message">
          <strong>Payment Failed:</strong> {userIsAdmin
            ? 'Your payment method was declined. Please update your payment method to avoid service interruption.'
            : 'Your payment method was declined. Please contact your account administrator to update the payment method.'}
          {daysRemaining > 0 && (
            <span className="payment-failure-banner__days">
              {' '}You have {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining in your grace period.
            </span>
          )}
        </div>

        {userIsAdmin && (
          <button
            className="payment-failure-banner__button"
            onClick={handleUpdatePayment}
            data-testid="update-payment-button"
          >
            Update Payment Method
          </button>
        )}

        {onClose && (
          <button
            className="payment-failure-banner__close"
            onClick={onClose}
            aria-label="Dismiss banner"
            data-testid="dismiss-banner-button"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

